document.addEventListener('DOMContentLoaded', () => {
    // --- Essential Elements ---
    const container = document.getElementById('main-collection-content');
    const productList = document.getElementById('collection-product-list');
    const loadMoreButton = document.getElementById('load-more-button');
    const loadingSpinner = document.getElementById('loading-spinner');

    // --- Basic Check ---
    if (!container || !productList || !loadMoreButton || !loadingSpinner) {
        console.error('[Collection Loader] ERROR: Required page elements not found.');
        return;
    }

    // --- Get Data from Container ---
    const collectionHandle = container.dataset.collectionHandle;
    const targetCountryCode = container.dataset.countryCode;
    const currencySymbol = container.dataset.currencySymbol;
    const currencyCode = container.dataset.currencyCode;
    const storefrontToken = container.dataset.storefrontApiToken;
    // Read the UPDATED API version from the container
    const apiVersion = container.dataset.apiVersion || '2025-04'; // Default to latest if attribute missing
    const storefrontEndpoint = `/api/${apiVersion}/graphql.json`;
    const rootUrl = container.dataset.rootUrl || '/';

    console.log(`[Collection Loader] Using API Version: ${apiVersion}`); // Log the version being used

    // --- Get Translations ---
    const translations = {
        priceUnavailable: container.dataset.translationPriceUnavailable || 'Price unavailable',
        fastShipping: container.dataset.translationFastShipping || 'Fast Shipping',
        viewProduct: container.dataset.translationViewProduct || 'View Product'
    };

    // --- Critical Data Check ---
    if (!collectionHandle || !targetCountryCode || !storefrontToken) {
         console.error('[Collection Loader] ERROR: Missing critical data attributes (handle, country, or token).');
         return;
    }

    // --- State Variables ---
    let currentCursor = null;
    let hasNextPage = true;
    let isLoading = false;
    const productsPerPage = 50;
    const fetchDelay = 150;

    // --- GraphQL Query (Using category { name } - Correct for 2024-10+) ---
    const GQL_QUERY = `
      query GetProductsInCollectionWithCountry(
        $collectionHandle: String!
        $first: Int!
        $after: String
        $country: CountryCode = ${targetCountryCode}
        $language: LanguageCode = ${Shopify.locale.toUpperCase()}
      ) @inContext(country: $country, language: $language) {
        collectionByHandle(handle: $collectionHandle) {
          id
          products(first: $first, after: $after) {
            edges {
              cursor
              node {
                id
                title
                handle
                category {        # <<< Request category object (available in 2024-10+)
                  name          # <<< Get name from category object
                }
                featuredImage {
                  url(transform: {maxWidth: 300, maxHeight: 300, crop: CENTER})
                  altText
                  width
                  height
                }
                countryMetafield: metafield(namespace: "custom", key: "country_code") { value }
                offerName: metafield(namespace: "custom", key: "offer_name") { value }
                discounts: metafield(namespace: "custom", key: "discounts") { value }
                redirect: metafield(namespace: "custom", key: "redirect") { value }
                variants(first: 1) {
                  nodes {
                    id
                    price { amount currencyCode }
                    compareAtPrice { amount currencyCode }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;


    // --- Fetch Function ---
    async function fetchProducts() {
        if (isLoading || !hasNextPage) {
             if (!hasNextPage) { if(loadingSpinner) loadingSpinner.style.display = 'none'; if(loadMoreButton) loadMoreButton.style.display = 'none'; }
             return;
        }
        isLoading = true;
        if (loadingSpinner) loadingSpinner.style.display = 'block';

        try {
            const response = await fetch(storefrontEndpoint, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken},
                body: JSON.stringify({ query: GQL_QUERY, variables: { collectionHandle: collectionHandle, first: productsPerPage, after: currentCursor }})
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const jsonResponse = await response.json();
            if (jsonResponse.errors) {
                console.error('[Collection Loader] GraphQL Errors:', jsonResponse.errors);
                const errorMessage = jsonResponse.errors[0]?.message || 'GraphQL error fetching products.';
                // Check specifically for field-related errors after version change
                if (errorMessage.includes("doesn't exist on type")) {
                   console.error(`[Collection Loader] Field Error: The API version ${apiVersion} might not support a requested field. Verify schema or try removing fields.`);
                }
                throw new Error(errorMessage);
            }

            const collectionData = jsonResponse.data?.collectionByHandle;
            if (!collectionData || !collectionData.products) { hasNextPage = false; throw new Error('Invalid data structure received from GraphQL.'); }

            const products = collectionData.products.edges;
            const pageInfo = collectionData.products.pageInfo;

            // --- Filtering Logic (Strict Match Required) ---
            const filteredProducts = products.filter(edge => {
                const productNode = edge.node;
                if (!productNode) return false;
                const productCountry = productNode.countryMetafield?.value;
                return productCountry === targetCountryCode; // Strict match
            });

            if (filteredProducts.length === 0 && products.length > 0) {
                 console.warn(`[CL] WARNING: Products received, but none matched country '${targetCountryCode}'. Check metafield 'custom.country_code'.`);
            }

            // --- Render Products ---
            filteredProducts.forEach(edge => {
                const product = edge.node;
                if(product) {
                    const li = document.createElement('li');
                    li.innerHTML = generateProductHtml(product); // Generate HTML using category.name
                    productList.appendChild(li);
                }
            });

            // --- Update State ---
            currentCursor = pageInfo.endCursor;
            hasNextPage = pageInfo.hasNextPage;

            // --- Recursive Call or Finish ---
            if (hasNextPage) {
                isLoading = false; setTimeout(fetchProducts, fetchDelay);
            } else {
                if (loadingSpinner) loadingSpinner.style.display = 'none';
                if (loadMoreButton) loadMoreButton.style.display = 'none';
                isLoading = false;
            }

        } catch (error) {
            console.error('[Collection Loader] ERROR:', error);
            if (loadingSpinner) {
                loadingSpinner.innerHTML = `Error: ${error.message}`;
                loadingSpinner.style.display = 'block';
                loadingSpinner.style.color = 'red';
            }
            if (loadMoreButton) loadMoreButton.style.display = 'none';
            hasNextPage = false; isLoading = false;
        }
    }

    // --- Generate Product HTML (FULL STRUCTURE - Using product.category.name) ---
    function generateProductHtml(product) {
        if (!product) return '';

        const imageUrl = product.featuredImage?.url || '';
        const imageAlt = product.featuredImage?.altText || product.title || 'Product Image';
        const productBaseUrl = rootUrl === '/' ? '/' : (rootUrl.endsWith('/') ? rootUrl : rootUrl + '/');
        const productUrl = product.handle ? `${productBaseUrl}products/${product.handle}` : '#';

        // Determine Display Title
        let displayTitle = product.title || 'Untitled Product';
        if (displayTitle.includes(' - ')) displayTitle = displayTitle.split(' - ')[0].trim();
        if (product.redirect?.value !== 'true' && product.offerName?.value) displayTitle = product.offerName.value;

        // Get Category Name using product.category.name (should work with API v2024-10+)
        const categoryName = product.category?.name || '';

        // Generate Price HTML
        const priceHtml = generateVariantPriceHtml(product.variants?.nodes?.[0], product.discounts?.value, targetCountryCode, currencySymbol);

        // Build the full HTML structure
        return `
          <a href="${productUrl}" class="product-card">
            <img class="product_image"
                 src="${imageUrl}"
                 srcset="${imageUrl} 1x"
                 width="300"
                 height="300"
                 alt="${imageAlt}"
                 loading="lazy">

            <div class="product-card__info">
              ${categoryName ? `<span class="product-card__category">${categoryName}</span>` : ''}

              <span class="product_name" itemprop="name">
                ${displayTitle}
              </span>

              <div class="product-card__rating">
                <img src="https://cdn.shopify.com/s/files/1/0624/3827/7288/files/star_full.png?v=1740414589" alt="Star" width="15" height="15" loading="lazy">
                <img src="https://cdn.shopify.com/s/files/1/0624/3827/7288/files/star_full.png?v=1740414589" alt="Star" width="15" height="15" loading="lazy">
                <img src="https://cdn.shopify.com/s/files/1/0624/3827/7288/files/star_full.png?v=1740414589" alt="Star" width="15" height="15" loading="lazy">
                <img src="https://cdn.shopify.com/s/files/1/0624/3827/7288/files/star_full.png?v=1740414589" alt="Star" width="15" height="15" loading="lazy">
                <img src="https://cdn.shopify.com/s/files/1/0624/3827/7288/files/star_full.png?v=1740414589" alt="Star" width="15" height="15" loading="lazy">
              </div>

              ${priceHtml}

              <div class="product-card__shipping">
                 <span>${translations.fastShipping}</span>
              </div>

              <span class="product-card__view-button button button--small">
                ${translations.viewProduct}
              </span>
            </div>
          </a>
        `;
    }

    // --- Helper function for Price HTML (No Forced Decimals) ---
    function generateVariantPriceHtml(firstVariant, discountMetafieldValue, targetCountryCode, currencySymbol) {
        let priceHtml = '';
        let currentPriceNum = null;
        let originalPriceNum = null;

        // 1. Try parsing discount metafield
        if (discountMetafieldValue) {
            try {
                const discounts = discountMetafieldValue;
                const discountPairs = discounts.split('#');
                if (discountPairs.length > 0) {
                    const firstDiscount = discountPairs[0].split('|');
                    if (firstDiscount.length === 2) {
                        const discountedNum = parseFloat(firstDiscount[0]);
                        const originalNum = parseFloat(firstDiscount[1]);
                        if (!isNaN(discountedNum)) currentPriceNum = discountedNum;
                        if (!isNaN(originalNum) && originalNum > discountedNum) originalPriceNum = originalNum;
                    }
                }
            } catch (e) { console.error("Error parsing discount:", e); }
        }

        // 2. If no discount price, try variant price
        if (currentPriceNum === null && firstVariant?.price) {
            currentPriceNum = parseFloat(firstVariant.price.amount);
            if (firstVariant.compareAtPrice && parseFloat(firstVariant.compareAtPrice.amount) > currentPriceNum) {
                originalPriceNum = parseFloat(firstVariant.compareAtPrice.amount);
            }
        }

        // 3. Format the price HTML
        if (currentPriceNum !== null) {
            const currentPriceStr = String(currentPriceNum);
            const originalPriceStr = originalPriceNum !== null ? String(originalPriceNum) : '';

            const roHuPlCsCzBg = ['RO', 'HU', 'PL', 'CS', 'CZ', 'BG'];
            if (roHuPlCsCzBg.includes(targetCountryCode)) { // Price AFTER symbol
                priceHtml = `<span class="price"><span class="current_price">${currentPriceStr}${currencySymbol}</span>`;
                if (originalPriceStr !== '') priceHtml += ` <span class="was_price">${originalPriceStr}${currencySymbol}</span>`;
                priceHtml += `</span>`;
            } else { // Price BEFORE symbol (default)
                priceHtml = `<span class="price"><span class="current_price">${currencySymbol}${currentPriceStr}</span>`;
                 if (originalPriceStr !== '') priceHtml += ` <span class="was_price">${currencySymbol}${originalPriceStr}</span>`;
                priceHtml += `</span>`;
            }
        } else {
             // 4. Fallback if no price found
            priceHtml = `<span class="price">${translations.priceUnavailable}</span>`;
        }
        return priceHtml;
    }

    // --- Initial Load ---
    loadMoreButton.style.display = 'none';
    if (loadingSpinner) loadingSpinner.style.display = 'block';
    fetchProducts();

});